import { useNavigate, useParams } from 'react-router-dom';
import type { GeoBrowseGeneStudy } from '../../types';
import { useGeoBrowseContext } from '../../context/GeoBrowseContext';
import { useGeoBrowseQuery } from '../../hooks/useGeoBrowseThresholds';
import { fetchGeneStudies, findGeneBySymbol } from '../../services/geoBrowseApi';
import {
  formatDiseaseName,
  formatLog2Fc,
  formatNumber,
  formatPadj,
} from '../../utils/formatters';
import { buildStudyDetailPath, comparisonRowKey, isWholeStudyComparison } from '../../utils/studyHelpers';
import BackLink from '../BackLink';
import BrowseDataTable, { type BrowseColumn } from '../BrowseDataTable';
import StatCards from '../StatCards';
import { StatusBadge } from '../ui/GeoBrowseUi';
import styles from '../../GeoBrowse.module.css';

function FcCell({ value }: { value: number }) {
  const isUp = value >= 0;
  return (
    <span className={isUp ? styles.fcUp : styles.fcDown}>
      {isUp ? '▲ ' : '▼ '}
      {formatLog2Fc(value)}
    </span>
  );
}

export default function GeneDetailView() {
  const { geneSymbol = '' } = useParams();
  const navigate = useNavigate();
  const { thresholds, refreshKey, buildPathWithThresholds } = useGeoBrowseContext();
  const gene = findGeneBySymbol(geneSymbol);

  const { data, loading, error } = useGeoBrowseQuery(
    () => fetchGeneStudies(geneSymbol, thresholds),
    [geneSymbol, thresholds.padj, thresholds.fc, refreshKey],
    Boolean(geneSymbol),
  );

  const columns: BrowseColumn<GeoBrowseGeneStudy>[] = [
    {
      id: 'study',
      label: 'Study',
      sortValue: r => r.title,
      render: r => (
        <>
          <span className={styles.studyTitle}>{r.title}</span>
          <span className={styles.cellSecondary}>
            {r.accession} · {r.year}
            {!isWholeStudyComparison(r) ? ` · ${r.comparison_id}` : ''}
          </span>
        </>
      ),
    },
    {
      id: 'disease',
      label: 'Disease',
      sortValue: r => r.cui_name,
      render: r => formatDiseaseName(r.cui_name),
    },
    {
      id: 'log2fc',
      label: 'log2 FC',
      sortValue: r => r.log2FoldChange,
      render: r => <FcCell value={r.log2FoldChange} />,
    },
    {
      id: 'padj',
      label: 'padj',
      sortValue: r => r.padj,
      render: r => formatPadj(r.padj),
    },
    {
      id: 'baseMean',
      label: 'baseMean',
      sortValue: r => r.baseMean,
      render: r => formatNumber(Math.round(r.baseMean)),
    },
  ];

  if (!geneSymbol) {
    return (
      <div className={styles.empty}>
        Select a gene from the Genes view to see its per-study evidence.
      </div>
    );
  }

  if (loading) return <div className={styles.loading}>Loading gene studies…</div>;
  if (error) return <div className={styles.error}>{error}</div>;

  return (
    <>
      <BackLink to={buildPathWithThresholds('/geo-browse/genes')} label="Genes" />

      <div className={styles.detailTitleRow}>
        <h1 className={styles.detailTitle}>{geneSymbol}</h1>
        {gene && (
          <div className={styles.detailBadges}>
            <StatusBadge label={gene.type_of_gene} variant="neutral" />
            <StatusBadge label={`GeneID ${gene.gene_id}`} variant="neutral" />
          </div>
        )}
      </div>

      {gene && (
        <StatCards
          items={[
            {
              label: 'Diseases significant',
              value: gene.n_diseases_sig,
              sub: `of ${gene.n_diseases} tested`,
            },
            {
              label: 'Studies significant',
              value: formatNumber(gene.n_studies_sig),
              sub: `of ${formatNumber(gene.n_studies)} tested`,
            },
            { label: 'DEG rows', value: formatNumber(gene.n_deg_rows) },
            { label: 'Significant rows', value: formatNumber(gene.n_sig_rows) },
          ]}
        />
      )}

      <div className={styles.sectionCard}>
        <h2 className={styles.sectionCardTitle}>Per-study evidence</h2>
        <p className={styles.sectionCardHint}>
          padj ≤ {thresholds.padj}, |log2FC| ≥ {thresholds.fc}
        </p>

        {!data?.length ? (
          <div className={styles.empty}>No significant studies found at current thresholds.</div>
        ) : (
          <BrowseDataTable
            rows={data}
            columns={columns}
            rowKey={r => comparisonRowKey(r)}
            searchPlaceholder="Search study or disease…"
            searchFilter={(row, q) =>
              `${row.title} ${row.accession} ${row.comparison_id} ${row.cui_name}`.toLowerCase().includes(q)
            }
            onRowClick={row =>
              navigate(
                buildPathWithThresholds(
                  buildStudyDetailPath(row.cui, row.accession, row.comparison_id),
                ),
              )
            }
          />
        )}
      </div>
    </>
  );
}
